import React from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Chip,
  useTheme,
  alpha,
  Avatar,
} from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

function SubOrderPaymentCard({
  paymentList,
  onDelete,
  onSendForApproval,
  onApprove,
  onReject,
  onPay,
  formatCurrency,
  filterDate,
}) {
  const theme = useTheme();

  const getStatusConfig = (status) => {
    const configs = {
      PAID: {
        color: theme.palette.cyan?.main || "#06b6d4",
        bgcolor: alpha(theme.palette.cyan?.main || "#06b6d4", 0.15),
        label: "Paid",
      },
      APPROVED: {
        color: theme.palette.success.main,
        bgcolor: alpha(theme.palette.success.main, 0.15),
        label: "Approved",
      },
      REJECTED: {
        color: theme.palette.error.main,
        bgcolor: alpha(theme.palette.error.main, 0.15),
        label: "Rejected",
      },
      AWAITING_APPROVAL: {
        color: theme.palette.info.main,
        bgcolor: alpha(theme.palette.info.main, 0.15),
        label: "Awaiting Approval",
      },
      DRAFT: {
        color: theme.palette.grey[700],
        bgcolor: alpha(theme.palette.grey[500], 0.15),
        label: "Draft",
      },
    };
    return configs[status] || configs.DRAFT;
  };

  if (!paymentList || paymentList.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 4,
          px: 2,
          bgcolor: alpha(theme.palette.grey[500], 0.05),
          borderRadius: 1.5,
          border: `1px dashed ${alpha(theme.palette.grey[500], 0.3)}`,
        }}
      >
        <PaymentIcon
          sx={{
            fontSize: 40,
            color: theme.palette.grey[400],
            mb: 1,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          No payment records found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={2}>
        {paymentList.map((item, index) => {
          const statusConfig = getStatusConfig(item.status);

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id || index}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  transition: "all 0.2s ease-in-out",
                  cursor: "pointer",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 8px ${alpha(
                      theme.palette.common.black,
                      0.1
                    )}`,
                  },
                }}
              >
                {/* Header - Date and Amount */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mr: 1,
                      }}
                    >
                      <PaymentIcon sx={{ fontSize: "16px" }} />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">
                      {filterDate(item.paymentDate)}
                    </Typography>
                  </Box>
                  <Chip
                    label={formatCurrency(item.amount || 0)}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.success.main, 0.15),
                      color: theme.palette.success.dark,
                      border: `1px solid ${alpha(
                        theme.palette.success.main,
                        0.3
                      )}`,
                    }}
                  />
                </Box>

                {/* Status - Highlighted */}
                <Box sx={{ mb: 1 }}>
                  <Chip
                    label={statusConfig.label}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      bgcolor: statusConfig.bgcolor,
                      color: statusConfig.color,
                      border: `1px solid ${alpha(statusConfig.color, 0.3)}`,
                    }}
                  />
                </Box>

                {/* Created By - Left Aligned */}
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", textAlign: "left" }}
                  >
                    Created by: <strong>{item.addedBy || "N/A"}</strong>
                  </Typography>
                </Box>

                {/* Created By - Left Aligned */}
                {item.approvedBy && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", textAlign: "left" }}
                    >
                      {item.status === "APPROVED" ||
                        (item.status === "PAID" && (
                          <>
                            Approved by:{" "}
                            <strong>{item.approvedBy || "N/A"}</strong>
                          </>
                        ))}
                      {item.status === "REJECTED" && (
                        <>
                          Rejected by:{" "}
                          <strong>{item.approvedBy || "N/A"}</strong>
                        </>
                      )}
                    </Typography>
                  </Box>
                )}

                {/* Note - Left Aligned */}
                {item.note && (
                  <Box sx={{ mb: 1, flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        lineHeight: 1.4,
                      }}
                    >
                      {item.note}
                    </Typography>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    pt: 1,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  {/* Send for Approval - Only show when DRAFT */}
                  <RoleBasedRender
                    allowedRoles={["SUB_ORDER_PAYMENT_CREATE", "ROLE_ADMIN"]}
                  >
                    {item.status === "DRAFT" && (
                      <Tooltip title="Send for Approval" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSendForApproval &&
                              onSendForApproval(item.paymentId);
                            console.log(
                              "Send for approval clicked for paymentId:",
                              item.paymentId
                            );
                          }}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                          }}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </RoleBasedRender>

                  {/* Approve Button - Only show when AWAITING_APPROVAL */}
                  <RoleBasedRender
                    allowedRoles={["SUB_ORDER_PAYMENT_APPROVE", "ROLE_ADMIN"]}
                  >
                    {item.status === "AWAITING_APPROVAL" && (
                      <Tooltip title="Approve" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onApprove && onApprove(item.paymentId);
                          }}
                          sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.success.main, 0.2),
                            },
                          }}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </RoleBasedRender>

                  {/* Reject Button - Only show when AWAITING_APPROVAL */}
                  <RoleBasedRender
                    allowedRoles={["SUB_ORDER_PAYMENT_APPROVE", "ROLE_ADMIN"]}
                  >
                    {item.status === "AWAITING_APPROVAL" && (
                      <Tooltip title="Reject" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onReject && onReject(item.paymentId);
                          }}
                          sx={{
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            color: theme.palette.error.main,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.error.main, 0.2),
                            },
                          }}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </RoleBasedRender>

                  {/* Pay Button - Only show when APPROVED */}
                  <RoleBasedRender
                    allowedRoles={["SUB_ORDER_PAYMENT_CREATE", "ROLE_ADMIN"]}
                  >
                    {item.status === "APPROVED" && (
                      <Tooltip title="Process Payment" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPay && onPay(item.paymentId);
                          }}
                          sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.success.main, 0.2),
                            },
                          }}
                        >
                          <AccountBalanceWalletIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </RoleBasedRender>

                  {/* Delete/Close Button - Only show when DRAFT or REJECTED */}
                  <RoleBasedRender
                    allowedRoles={["SUB_ORDER_PAYMENT_DELETE", "ROLE_ADMIN"]}
                  >
                    {(item.status === "DRAFT" ||
                      item.status === "REJECTED") && (
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(item.paymentId);
                          }}
                          sx={{
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            color: theme.palette.error.main,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.error.main, 0.2),
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </RoleBasedRender>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

SubOrderPaymentCard.propTypes = {
  paymentList: PropTypes.array.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSendForApproval: PropTypes.func,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onPay: PropTypes.func,
  formatCurrency: PropTypes.func.isRequired,
  filterDate: PropTypes.func.isRequired,
};

export default SubOrderPaymentCard;
